import React from 'react';
import { OrderIdBadge } from './shared';

export interface TimelineStage {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'pending';
  completedAt?: string;
  estimatedDate?: string;
}

export interface OrderTimelineProps {
  orderId: string;
  orderCreatedAt: string;
  dueDate: string;
  currentStatus: string;
  stages: TimelineStage[];
  onStageUpdate?: (stageId: string, status: 'completed' | 'current' | 'pending') => void;
}

const OrderTimeline: React.FC<OrderTimelineProps> = ({
  orderId,
  orderCreatedAt,
  dueDate,
  currentStatus,
  stages,
  onStageUpdate
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStageIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'current':
        return (
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'pending':
        return (
          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getStageColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'border-green-500 bg-green-50';
      case 'current':
        return 'border-blue-500 bg-blue-50';
      case 'pending':
        return 'border-gray-300 bg-gray-50';
      default:
        return 'border-gray-300 bg-gray-50';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Order Timeline</h3>
        <div className="flex items-center justify-between text-sm text-gray-600">
          <OrderIdBadge id={orderId} compact />
          <span>Due: {formatDate(dueDate)}</span>
        </div>
      </div>

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
        
        <div className="space-y-6">
          {stages.map((stage, index) => (
            <div key={stage.id} className="relative flex items-start">
              {/* Stage icon */}
              <div className="relative z-10 mr-4">
                {getStageIcon(stage.status)}
              </div>
              
              {/* Stage content */}
              <div className={`flex-1 p-4 rounded-lg border-l-4 ${getStageColor(stage.status)}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{stage.title}</h4>
                  {stage.status === 'completed' && stage.completedAt && (
                    <span className="text-xs text-green-600 font-medium">
                      Completed {formatDate(stage.completedAt)}
                    </span>
                  )}
                  {stage.status === 'current' && stage.estimatedDate && (
                    <span className="text-xs text-blue-600 font-medium">
                      Estimated: {formatDate(stage.estimatedDate)}
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-3">{stage.description}</p>
                
                {/* Action buttons for current stage */}
                {stage.status === 'current' && onStageUpdate && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onStageUpdate(stage.id, 'completed')}
                      className="px-3 py-1 text-xs bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                    >
                      Mark Complete
                    </button>
                    <button
                      onClick={() => onStageUpdate(stage.id, 'pending')}
                      className="px-3 py-1 text-xs bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                    >
                      Mark Pending
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            Created: {formatDate(orderCreatedAt)}
          </span>
          <span className="text-gray-600">
            Current Status: <span className="font-medium text-blue-600">{currentStatus}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default OrderTimeline;
